import { ApolloQueryResult } from "@apollo/client";
import {
  IContentRaw,
  UninitiatedBlockData,
} from "components/admin/custom-blocks/types";
import { Layout } from "components/layout";
import { SharingBar } from "components/sharing-bar";
import { addApolloState, initializeApollo } from "lib/hooks/apollo-client";
import { GetServerSideProps } from "next";
import {
  GetPageContentDocument,
  GetPageContentQueryResult,
  GetPageContentQueryVariables,
} from "lib/graphql-generated";
import slugify from "slugify";
import { RenderContent } from "components/content-renderer";
import Link from "next/link";
interface Props {
  content: IContentRaw;
}

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const client = initializeApollo();

  const { data }: ApolloQueryResult<GetPageContentQueryResult["data"]> =
    await client.query({
      query: GetPageContentDocument,
      variables: {
        title: "faq",
      } as GetPageContentQueryVariables,
    });
  return addApolloState(client, {
    props: {
      content: data.pages_by_pk.content,
    } as Props,
  });
};
export const ContactUs = ({ content }: Props) => {
  const faqBlock = content.data.find((block) => block.label === "questions")
    .data as UninitiatedBlockData<"key-value">;

  return (
    <>
      <Layout pageName='FAQ' className='space-y-8 relative'>
        <article className='space-y-8'>
          <header className='bg-green-light 2xl:px-2'>
            <section className='mx-auto max-w-screen-2xl p-4 pt-7 flex'>
              <h1 className='font-header tracking-wide uppercase text-5xl text-center my-auto mx-auto text-white'>
                Making Contact
              </h1>
            </section>
          </header>
          <section className='space-y-16 pb-8 flex flex-col justify-around xl:grid xl:grid-cols-2 xl:space-y-0 xl:gap-16 max-w-screen-xl mx-auto '>
            <article className='px-12 text-center xl:text-left max-w-md mx-auto xl:mx-0 xl:col-start-2'>
              <h2 className='font-header tracking-wide uppercase text-2xl'>
                Communicate with us
              </h2>
              <dl className='flex flex-row flex-wrap md:text-center max-w-md mx-auto '>
                <div className='text-center mx-auto xl:mx-0 '>
                  <dt className='inline mr-4'>Email:</dt>
                  <dd className='inline'>
                    <a href='mailto:books@lighthousebookshop.com'>
                      books@lighthousebookshop.com
                    </a>
                  </dd>
                </div>
                <div className='text-center mx-auto xl:mx-0'>
                  <dt className='inline mr-4'>Phone:</dt>
                  <dd className='inline'>
                    <a href='tel:01316629112' className=''>
                      0131 662 9112
                    </a>
                  </dd>
                </div>
              </dl>
            </article>
            <article className='px-12 text-center xl:text-left max-w-md mx-auto xl:col-start-2 xl:mx-0 '>
              <h2 className='font-header tracking-wide uppercase text-2xl'>
                Find us on the high street
              </h2>
              <address className='not-italic'>
                Lighthouse - Edinburgh's Radical Bookshop
                <br />
                43-45 West Nicolson Street
                <br />
                Edinburgh, Scotland, EH8 9DB
              </address>
            </article>
            <section className='h-full w-full max-w-md xl:col-start-1 xl:row-span-3 xl:row-start-1 m-auto flex flex-col'>
              <a
                aria-labelledby='viewLargeMap'
                href='https://www.openstreetmap.org/?mlat=55.94460&amp;mlon=-3.18550#map=19/55.94460/-3.18550'
                className='cursor-pointer border hover:border-green-default'>
                <img
                  src='/assets/images/map.png'
                  alt='Map showing Lighthouse Bookshops location'
                />
              </a>
              <br />
              <small className='mb-auto'>
                <a
                  id='viewLargeMap'
                  href='https://www.openstreetmap.org/?mlat=55.94460&amp;mlon=-3.18550#map=19/55.94460/-3.18550'
                  className='hover:underline hover:text-red-default'>
                  Click to View Larger Map
                </a>
              </small>
            </section>
            <article className='px-12 md:text-center xl:text-left max-w-md mx-auto xl:col-start-2 xl:mx-0 '>
              <h2 className='font-header tracking-wide uppercase text-2xl text-center xl:text-left'>
                Our Social Media
              </h2>
              <div className='flex flex-row space-x-4 justify-center xl:justify-start'>
                <SharingBar
                  className='h-4'
                  url=''
                  text="Checkout Lighthouse Bookshop, Edinburgh's Radical Bookshop"
                />
              </div>
            </article>
          </section>
        </article>
        <article className='bg-green-light px-2 md:px-4 2xl:px-2'>
          <header className='max-w-screen-2xl flex flex-row flex-wrap  mx-auto'>
            <h1 className='font-header tracking-wide text-white text-9xl mx-auto pt-8 w-full text-center md:flex-1 md:text-12xl md:my-auto md:pt-16'>
              FAQs
            </h1>
            <nav className='bg-white w-full p-8 md:flex-1 bg-green-light text-white my-auto'>
              <h2 className='md:hidden pb-2 underline'>Table Of Contents</h2>
              <ul id='faq' className='max-w-2xs md:mx-auto'>
                {faqBlock.map((ob, i) => (
                  <li
                    key={i}
                    className='group border-2 border-green-light rounded-lg md:px-4 md:py-2 font-header tracking-wide uppercase  md:text-left md:text-xl list-disc md:list-none ml-4 md:ml-0'>
                    <a className='h-full w-full hover:underline' href={`#${i}`}>
                      {ob.key}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          </header>
          <section>
            <ul className='max-w-screen-xl mx-auto px-4 xl:px-0 py-8 space-y-8 prose flex flex-col'>
              {faqBlock.map((block, i) => (
                <li
                  key={i}
                  id={i.toString()}
                  className='bg-white rounded p-4 pt-12 flex'>
                  <div className='mx-auto prose w-full'>
                    <h3 className='font-bold text-lg'>{block.key}</h3>
                    <RenderContent content={block.value} />
                  </div>
                </li>
              ))}
            </ul>
            <div className='sticky bottom-8 flex w-full max-w-screen-xl mx-auto'>
              <Link href='#faq'>
                <a className=' flex flex-row px-2  border-2 border-blue-default bg-blue-default justify-center rounded  group ml-auto '>
                  <span className=''>Back to Top</span>
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    className='h-6 w-6  rounded-full bg-blue-default transform group-hover:-translate-y-2 transition-transform ease-in mr-auto my-auto ml-2 '
                    fill='none'
                    viewBox='0 0 24 24'
                    stroke='currentColor'>
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M9 11l3-3m0 0l3 3m-3-3v8m0-13a9 9 0 110 18 9 9 0 010-18z'
                    />
                  </svg>
                </a>
              </Link>
            </div>
          </section>
        </article>
      </Layout>
    </>
  );
};

export default ContactUs;
